import React, { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useNotificationsByUser, useNotificationsByRole, useMarkAsRead, useMarkAllAsReadByUser, useMarkAllAsReadByRole } from '@/hooks/useNotifications'
import { getNotificationIcon, getNotificationColor, formatTimeAgo } from '@/types/notification'
import type { Notification } from '@/types/notification'

interface NotificationDropdownProps {
  userId?: string
  role?: string
}

export const NotificationDropdown: React.FC<NotificationDropdownProps> = ({
  userId,
  role,
}) => {
  const navigate = useNavigate()
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Fetch notifications and unread count
  const { data: userNotifications, isLoading: isLoadingUser } = useNotificationsByUser(
    userId || '',
    { limit: 5 }
  )
  const { data: roleNotifications, isLoading: isLoadingRole } = useNotificationsByRole(
    role || '',
    { limit: 5 }
  )

  // Mutations
  const markAsRead = useMarkAsRead()
  const markAllAsReadByUser = useMarkAllAsReadByUser()
  const markAllAsReadByRole = useMarkAllAsReadByRole()

  // Determine which data to use
  const isLoading = userId ? isLoadingUser : isLoadingRole
  const notifications = userId
    ? ((userNotifications as { notifications?: Notification[] })?.notifications || [])
    : ((roleNotifications as { notifications?: Notification[] })?.notifications || [])
  // Calculate unread count from notifications data (avoid extra API call)
  const unreadCount = notifications.filter((n: Notification) => !n.isRead).length

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleNotificationClick = async (notification: Notification) => {
    // Mark as read if not already read
    if (!notification.isRead) {
      try {
        await markAsRead.mutateAsync(notification.id)
      } catch (error) {
        // Silently ignore error - don't block navigation
      }
    }

    // Navigate to actionLink if available
    if (notification.actionLink) {
      navigate(notification.actionLink)
    }

    setIsOpen(false)
  }

  const handleMarkAllAsRead = async () => {
    if (userId) {
      await markAllAsReadByUser.mutateAsync(userId)
    } else if (role) {
      await markAllAsReadByRole.mutateAsync(role)
    }
  }

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-white/5 transition-colors"
      >
        <span className="material-symbols-outlined text-slate-600 dark:text-slate-300 text-[24px]">
          {unreadCount > 0 ? 'notifications_active' : 'notifications'}
        </span>

        {/* Unread Count Badge */}
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center px-1 bg-rose-500 text-white text-[10px] font-black rounded-full shadow-lg shadow-rose-500/30 animate-pulse">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown Content */}
      {isOpen && (
        <div className="absolute top-full right-0 mt-2 w-80 max-w-[calc(100vw-2rem)] bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-100 dark:border-white/10 overflow-hidden z-50">
          {/* Header */}
          <div className="px-4 py-3 border-b border-slate-100 dark:border-white/10 flex items-center justify-between">
            <h3 className="text-sm font-black text-slate-900 dark:text-white">
              Notifikasi
            </h3>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllAsRead}
                disabled={markAllAsReadByUser.isPending || markAllAsReadByRole.isPending}
                className="text-xs font-bold text-primary hover:underline disabled:opacity-50"
              >
                Tandai Semua Dibaca
              </button>
            )}
          </div>

          {/* Notifications List */}
          <div className="max-h-80 overflow-y-auto">
            {isLoading ? (
              <div className="p-4 space-y-3">
                {[1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="h-16 bg-slate-100 dark:bg-white/5 rounded-xl animate-pulse"
                  />
                ))}
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center">
                <div className="size-12 bg-slate-100 dark:bg-white/5 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="material-symbols-outlined text-slate-300 dark:text-slate-600 text-[24px]">
                    notifications_off
                  </span>
                </div>
                <p className="text-sm font-bold text-slate-400 dark:text-slate-500">
                  Tidak ada notifikasi
                </p>
              </div>
            ) : (
              <div className="divide-y divide-slate-50 dark:divide-white/5">
                {notifications.map((notification: Notification) => (
                  <NotificationDropdownItem
                    key={notification.id}
                    notification={notification}
                    onClick={() => handleNotificationClick(notification)}
                    isMarking={markAsRead.isPending}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

interface NotificationDropdownItemProps {
  notification: Notification
  onClick: () => void
  isMarking?: boolean
}

const NotificationDropdownItem: React.FC<NotificationDropdownItemProps> = ({
  notification,
  onClick,
  isMarking = false,
}) => {
  const icon = getNotificationIcon(notification.type)
  const colorClass = getNotificationColor(notification.type)

  return (
    <button
      onClick={onClick}
      disabled={isMarking}
      className={`w-full flex items-start gap-3 p-4 text-left transition-all hover:bg-slate-50 dark:hover:bg-white/5 ${
        !notification.isRead ? 'bg-primary/5 border-l-3 border-primary' : ''
      } disabled:opacity-50`}
    >
      <div className={`size-9 rounded-lg flex items-center justify-center shrink-0 ${colorClass}`}>
        <span className={`material-symbols-outlined text-[18px] ${!notification.isRead ? 'filled' : ''}`}>{icon}</span>
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-xs line-clamp-1 ${
          !notification.isRead ? 'font-black text-slate-900 dark:text-white' : 'font-bold text-slate-700 dark:text-slate-300'
        }`}>
          {notification.title}
        </p>
        <p className="text-[11px] text-slate-600 dark:text-slate-400 mt-0.5 line-clamp-2">
          {notification.message}
        </p>
        <p className="text-[10px] text-slate-500 dark:text-slate-500 mt-1">
          {formatTimeAgo(notification.createdAt)}
        </p>
      </div>
      {!notification.isRead && (
        <div className="size-2.5 bg-primary rounded-full shrink-0 mt-2 shadow-sm shadow-primary/50" />
      )}
    </button>
  )
}

export default NotificationDropdown